import { AtomEffect, AtomOptions, SerializableParam } from 'recoil';
import { partial, partialRight } from 'lodash';
import { getPersistedRecoilValue, persistRecoilValue } from './persistence';

const devLogPrefix = '[Recoil]:' as const;
const devLog = partial(console.log, devLogPrefix);

// #region Options
export interface CustomAtomEffectOptions<TValue = unknown, TParam extends SerializableParam = SerializableParam> {
  key?: AtomOptions<TValue>['key'];
  param?: TParam;
  verbose?: boolean;
}
const defaultOptions: CustomAtomEffectOptions = {
  verbose: false,
} as const;

export type CustomAtomEffect<T = any> = (
  ...args: [...param: Parameters<AtomEffect<T>>, options?: CustomAtomEffectOptions]
) => ReturnType<AtomEffect<T>>;

export const withOptions =
  <TEffect extends AtomEffect<any>, TOptions extends CustomAtomEffectOptions>(effect: TEffect) =>
  (options: TOptions): CustomAtomEffect<TEffect extends AtomEffect<infer TValue> ? TValue : any> =>
    partialRight(effect, options);
export const effectsWithOptions = <TEffect extends AtomEffect<any>, TOptions extends CustomAtomEffectOptions>(
  effects: TEffect[],
  options: TOptions
): CustomAtomEffect<TEffect extends AtomEffect<infer TValue> ? TValue : any>[] =>
  effects.map(e => (e.length === 1 ? withOptions(e)(options) : e));
// #endregion

// #region Effects: Occur on init, and re-init if atom not yet accessed at render (e.g. accessed via `Snapshot` before having been accessed directly).
export const logOnSet: CustomAtomEffect = ({ node: { key }, onSet }, options = defaultOptions) => {
  options.verbose && devLog(`Logging enabled for key '${key}'.`);
  onSet((newValue, oldValue, isReset) => devLog(`${isReset ? 'Reset' : 'Set'} '${key}'`, { newValue, oldValue }));
};
export const logOnSetWith = withOptions(logOnSet);

export const logOnCleanup: CustomAtomEffect = ({ node: { key } }, options = defaultOptions) => {
  options.verbose && devLog(`Cleanup logging enabled for key '${key}'.`);
  return () => devLog(`Cleaning up key '${key}'.`);
};
export const logOnCleanupWith = withOptions(logOnCleanup);

export const persistOnSet: CustomAtomEffect = ({ node: { key }, onSet }, options = defaultOptions) => {
  options.verbose && devLog(`Persistence enabled for key '${key}'.`);
  onSet((newValue, _oldValue, isReset) => persistRecoilValue(key, isReset ? undefined : newValue));
};
export const persistOnSetWith = withOptions(persistOnSet);

export const loadPersistedValue: CustomAtomEffect = ({ node: { key }, setSelf }, options = defaultOptions) => {
  options.verbose && devLog(`Persistence loading enabled for key '${key}'.`);
  const persistedValue = getPersistedRecoilValue(key);
  if (persistedValue !== undefined) {
    options.verbose && devLog(`Loading persisted value for '${key}'`, persistedValue);
    setSelf(persistedValue);
  }
};
export const loadPersistedValueWith = withOptions(loadPersistedValue);
// #endregion
